import React, { useEffect, useMemo, useState, forwardRef } from 'react';
import { optionsWithSelection, SelectSearch } from '~/ui/components';
import { MultiSelect } from '~/ui/components/MultiSelect/MultiSelect';
import { normalizeData } from '~/common/tickets';
import { debounce } from 'lodash';
import { useAwaitControl } from 'react-redux-await-control';
import { getTicketStatus } from '~/store/tickets/actions';

export type StatusSelectProps = {
  value?: number | number[] | string | null | undefined;
  onChange?: (value: any) => void;
  className?: string;
  width?: number;
  multiple?: boolean;
  allowedLabels?: string[];
};

export const ALLOWED_STATUS_NAMES = [
  'Abandono',
  'Aguardando tratativa',
  'Concluído',
  'Em tratativa',
  'Fechado',
  'Pendente Cliente',
  'Reaberto',
];

function normalizeStatusLabel(s: string): string {
  return s.toLowerCase().trim().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export const StatusSelect = forwardRef<HTMLDivElement, StatusSelectProps>(
  ({ value, onChange, className, width, multiple = false, allowedLabels }, ref) => {
    const [currentValue, setCurrentValue] = useState<{ value: string; label: string }>(null);
    const [searchString, setSearchString] = useState('');

    const awaitControl = useAwaitControl(getTicketStatus);
    const isLoading = awaitControl.isRunning();
    const allOptions = awaitControl.result();

    const optionsList = useMemo(() => {
      const base = allOptions?.filter((option) => ALLOWED_STATUS_NAMES.includes(option.name));
      const filtered = allowedLabels?.length
        ? base?.filter((option) => {
            const allowed = new Set(allowedLabels.map(normalizeStatusLabel));
            return allowed.has(normalizeStatusLabel(option.name));
          })
        : base;
      return filtered?.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [allOptions, allowedLabels]);

    const options = useMemo(() => {
      if (!searchString?.length) {
        return optionsList;
      }

      return optionsList?.filter((data) => data.name.toLowerCase().includes(searchString.toLowerCase().trim()));
    }, [optionsList, searchString]);

    const handleOnChange = (option: { value: string; label: string }) => {
      setCurrentValue(option);

      if (onChange) {
        onChange(option?.value);
      }
    };

    const handleMultiChange = (values: string[]) => {
      if (onChange) {
        onChange(values?.length ? values : undefined);
      }
    };

    const handleSearch = debounce((searchValue) => {
      setSearchString(searchValue);
    }, 500);

    useEffect(() => {
      if (!optionsList?.length) {
        awaitControl.start();
      }
    }, []);

    if (multiple) {
      return (
        <MultiSelect
          ref={ref}
          placeholder="Status"
          loading={isLoading}
          options={normalizeData(optionsList)}
          onChange={handleMultiChange}
          value={value as any}
          searchable
        />
      );
    }

    return (
      <SelectSearch
        ref={ref}
        searchValue={searchString}
        placeholder="Status"
        width={width}
        value={value ? { value: Number(value) } : null}
        onSearch={handleSearch}
        onChange={handleOnChange}
        loading={isLoading}
        options={optionsWithSelection(options, currentValue)}
        onClear={() => setSearchString('')}
        className={className}
      />
    );
  },
);
